import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteName, siteUrl, supportEmail, teamAuthor, socialLinks, twitterHandle } from 'config/site';

const DEFAULT_OG_IMAGE = `${siteUrl}/og-image.jpg`;

const PageSEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'article',
  datePublished,
  dateModified,
  author = teamAuthor,
  breadcrumbs = [],
  schemas = [],
  noIndex = false,
}) => {
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const lastModified = dateModified || datePublished || new Date().toISOString().split('T')[0];

  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      ...breadcrumbs.map((crumb, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: crumb.name,
        item: `${siteUrl}${crumb.path}`,
      })),
    ],
  } : null;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    ...(socialLinks.length > 0 ? { sameAs: socialLinks } : {}),
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: supportEmail,
    },
  };

  const allSchemas = [organizationSchema, ...(breadcrumbSchema ? [breadcrumbSchema] : []), ...schemas];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" type="text/plain" href={`${siteUrl}/llms.txt`} title="LLM-readable site summary" />
      <link rel="alternate" type="text/plain" href={`${siteUrl}/ai.txt`} title="AI crawler discovery" />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      <meta name="googlebot" content="index, follow" />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      {twitterHandle && <meta name="twitter:site" content={`@${twitterHandle}`} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {datePublished && <meta property="article:published_time" content={datePublished} />}
      {lastModified && <meta property="article:modified_time" content={lastModified} />}
      {author && <meta property="article:author" content={author} />}

      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default PageSEO;
